import React from "react";
import AuthenticatedAudio from "./AuthenticatedAudio.jsx";
import "../../assets/css/HistoriqueDetailModal.css";
import AnalysisDiagnostic from "./AnalysisDiagnostic.jsx";
import { getHistoryPresentation, getRecordingAvailabilityLabel, getAppointment, getReviewEvidence } from "../../utils/resultPresentation.js";

// Couleurs pilotées par les tokens --hist-* (index.css), cohérentes
// avec les badges de qualification de HistoriquesPage.
const getStatusStyle = (status) => {
  const value = Number(status);

  switch (value) {
    case 2:
      return { color: "var(--hist-success)", bg: "var(--hist-success-soft)" };
    case 3:
      return { color: "var(--hist-info)", bg: "var(--hist-info-soft)" };
    case 4:
      return { color: "var(--hist-warning)", bg: "var(--hist-warning-soft)" };
    case 1:
      return { color: "var(--hist-danger)", bg: "var(--hist-danger-soft)" };
    default:
      return { color: "var(--hist-gray)", bg: "var(--hist-gray-soft)" };
  }
};

const isSale = (status) => {
  return Number(status) === 2;
};

export default function HistoriqueDetailModal({ open, historique, onClose }) {
  if (!open || !historique) return null;
  const ai = historique.aiResponse || {};
  const presentation = getHistoryPresentation(historique);
  const appointment = getAppointment(historique);
  const reviewEvidence = getReviewEvidence(historique);
  const callbackRequested = !presentation.opposition && (ai.status === "CALLBACK"
    || ai.pendingQualification === "CALLBACK" || ai.manualReview?.intendedStatus === "CALLBACK"
    || Boolean(ai.dateCLB || ai.heureCallBack || ai.callbackScheduling?.requestedDate));
  const statusStyle = presentation.opposition ? getStatusStyle(1)
    : presentation.diagnostic || presentation.derived ? getStatusStyle(null) : getStatusStyle(historique.status);

  return (
    <div className="historiqueModalOverlay" onClick={onClose}>
      <div className="historiqueModal" onClick={(e) => e.stopPropagation()}>
        <div className="historiqueModalHeader">
          <div>
            <h3>Détail de l'appel</h3>
            <span
              className="historiqueModalStatusBadge"
              style={{ color: statusStyle.color, background: statusStyle.bg }}
            >
              <i
                className="bi bi-record-circle-fill"
                style={{ fontSize: "9px" }}
              />
              {presentation.diagnostic && !presentation.derived && "Qualification enregistrée : "}
              {presentation.qualification}
            </span>
            <AnalysisDiagnostic record={historique} />
          </div>

          <button
            className="historiqueCloseBtn"
            onClick={onClose}
            aria-label="Fermer"
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <div className="historiqueModalBody">
          <p className="historiqueSectionLabel">
            <i className="bi bi-person-vcard" /> Informations de l'appel
          </p>
          <div className="historiqueDetailGrid">
            <div className="historiqueDetailItem">
              <span>Nom</span>
              <strong>{historique.fiche?.nom || "-"}</strong>
            </div>

            <div className="historiqueDetailItem">
              <span>Numéro appelé</span>
              <strong>
                {historique.fiche?.phone || historique.calledNumber || "-"}
              </strong>
            </div>

            <div className="historiqueDetailItem">
              <span>Numéro appelant</span>
              <strong>{historique.callerNumber || "-"}</strong>
            </div>

            <div className="historiqueDetailItem">
              <span>Campagne</span>
              <strong>{historique.campagneId?.nomCompagne || "-"}</strong>
            </div>

            <div className="historiqueDetailItem">
              <span>Agent</span>
              <strong>{historique.agentIaId?.nomAgent || "-"}</strong>
            </div>

            <div className="historiqueDetailItem">
              <span>Canal</span>
              <strong>{historique.channelId || "-"}</strong>
            </div>

            <div className="historiqueDetailItem">
              <span>Durée</span>
              <strong>
                {historique.billsec ?? historique.callDuration ?? 0} sec
              </strong>
            </div>

            <div className="historiqueDetailItem">
              <span>Archive</span>
              <strong>
                {historique.archive === 1 ? "Archivé" : "Non archivé"}
              </strong>
            </div>
          </div>

          <div className="historiqueResponseBox">
            <h4>Réponse IA</h4>

            {isSale(historique.status) ? (
              <div className="historiqueDetailGrid">
                <div className="historiqueDetailItem">
                  <span>Nom</span>
                  <strong>{ai.nameUser || "-"}</strong>
                </div>

                <div className="historiqueDetailItem">
                  <span>Email</span>
                  <strong>{ai.mailUser || "-"}</strong>
                </div>

                <div className="historiqueDetailItem">
                  <span>Téléphone</span>
                  <strong>{ai.phoneUser || "-"}</strong>
                </div>

                <div className="historiqueDetailItem">
                  <span>Adresse</span>
                  <strong>{ai.adresseUser || "-"}</strong>
                </div>

                {appointment && <div className="historiqueDetailItem">
                  <span>Date RDV</span>
                  <strong>{appointment.date}</strong>
                </div>}

                {appointment && <div className="historiqueDetailItem">
                  <span>Heure RDV</span>
                  <strong>{appointment.time}</strong>
                </div>}

                <div className="historiqueDetailItem full">
                  <span>Description</span>
                  <strong>{ai.description || "-"}</strong>
                </div>
              </div>
            ) : (
              <div className="historiqueDescriptionOnly">
                {ai?.description || "-"}
              </div>
            )}
            {reviewEvidence && (
              <div className="historiqueDetailItem full" aria-label="Éléments de la qualification à vérifier">
                <span>{reviewEvidence.sourceLabel}</span>
                <blockquote>{reviewEvidence.quote}</blockquote>
                <small>Cet extrait ne confirme ni envoi d’email, ni rappel programmé, ni rendez-vous enregistré.</small>
              </div>
            )}
            {callbackRequested && (
              <div className="historiqueDetailGrid" aria-label="Rappel demandé">
                <div className="historiqueDetailItem">
                  <span>Date de rappel demandée</span>
                  <strong>{ai.dateCLB || ai.callbackScheduling?.requestedDate || "À confirmer"}</strong>
                </div>
                <div className="historiqueDetailItem">
                  <span>Heure de rappel demandée</span>
                  <strong>{ai.heureCallBack || ai.callbackScheduling?.requestedTime || "À confirmer"}</strong>
                </div>
              </div>
            )}
          </div>

          <div className="historiqueRecordBox">
            <h4>Enregistrement</h4>

            {historique.pathRecord ? (
              <AuthenticatedAudio pathRecord={historique.pathRecord} className="historiqueModalAudio" />
            ) : (
              <p>
                <i className="bi bi-volume-mute" />
                {getRecordingAvailabilityLabel(historique)}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
